import Axios from 'api/client';
import { Household, Transaction } from 'types/domain';
import { PaginatedData } from 'types/pagination';

interface CreateHouseholdParams {
    name: string;
    envelopeName?: string;
    notes?: string;
    primaryContactId?: number;
}

type UpdateHouseholdParams = Partial<CreateHouseholdParams>;

interface FetchHouseholdsParams {
    page?: number;
    limit?: number;
    search?: string;
    sort?: string;
}

const fetchHouseholds = (accountId: number, params = {} as FetchHouseholdsParams) => {
    return Axios.get<PaginatedData<Household>>(`/accounts/${accountId}/households`, {
        params,
    }).then(response => response.data);
};

const fetchHouseholdById = (accountId: number, householdId: number) =>
    Axios.get<Household>(`/accounts/${accountId}/households/${householdId}`).then(
        response => response.data,
    );

const updateHouseholdById = (
    accountId: number,
    householdId: number,
    params = {} as UpdateHouseholdParams,
) =>
    Axios.patch<Household>(
        `/accounts/${accountId}/households/${householdId}`,
        transformHouseholdParams(params),
    ).then(response => response.data);

const deleteHouseholdById = (accountId: number, householdId: number) =>
    Axios.delete(`/accounts/${accountId}/households/${householdId}`).then(
        response => response.data,
    );

const associateContactsToHousehold = (
    accountId: number,
    householdId: number,
    contactIds: number[],
) =>
    Axios.post<Household>(`/accounts/${accountId}/households/${householdId}/contacts/associate`, {
        contact_ids: contactIds,
    });

const disassociateContactsFromHousehold = (
    accountId: number,
    householdId: number,
    contactIds: number[],
) =>
    Axios.post<Household>(
        `/accounts/${accountId}/households/${householdId}/contacts/disassociate`,
        {
            contact_ids: contactIds,
        },
    );

const createHousehold = (accountId: number, params = {} as CreateHouseholdParams) =>
    Axios.post<Household>(
        `/accounts/${accountId}/households`,
        transformHouseholdParams(params),
    ).then(response => response.data);

const transformHouseholdParams = (formParams?: UpdateHouseholdParams) => ({
    name: formParams?.name,
    envelope_name: formParams?.envelopeName,
    notes: formParams?.notes,
    head_contact_id: formParams?.primaryContactId,
});

interface HouseholdGivingSummaryContact {
    id: number;
    first_name: string;
    last_name: string;
    total_donated: number;
}
interface HouseholdGivingSummaryResponse {
    type: 'all_time' | 'year_to_date' | 'last_year';
    transactions: Array<{
        id: number;
        amount: number;
        contact_id: number;
        email: string;
        first_name: string;
        last_name: string;
        number: string;
        transacted_at: string;
    }>;
    aggregates_by_date_range: {
        total: number;
        largest_donation: null | Transaction;
    };
}

const fetchHouseholdChartStats = (
    accountId: number,
    householdId: number,
    type: HouseholdGivingSummaryResponse['type'],
) => {
    return Axios.get<HouseholdGivingSummaryResponse>(
        `/accounts/${accountId}/metrics/household-giving-summary`,
        {
            params: {
                household_id: householdId,
                type,
            },
        },
    ).then(response => response.data);
};

export type { HouseholdGivingSummaryResponse, HouseholdGivingSummaryContact };

export {
    fetchHouseholds,
    fetchHouseholdById,
    fetchHouseholdChartStats,
    createHousehold,
    updateHouseholdById,
    deleteHouseholdById,
    associateContactsToHousehold,
    disassociateContactsFromHousehold,
};
